import { getListedCfdInstrument } from './cfdInstruments.ts';

export const VALORE_STREAM_URL = 'wss://stream.valore.markets';

export interface ValoreQuote {
  symbol: string;
  bid: number;
  ask: number;
  mid: number;
  timestamp: number;
  currency?: string;
}

function positiveNumber(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

export function parseQuoteTimestamp(value: unknown): number | null {
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value))) {
    const number = Number(value);
    const timestamp = number < 1e12 ? number * 1000 : number;
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
  }
  if (typeof value === 'string') {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
  }
  return null;
}

export function parseValoreQuote(symbol: string, value: unknown, receivedAt = Date.now()): ValoreQuote | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  const listing = getListedCfdInstrument(symbol);
  const sourceTimestamp = parseQuoteTimestamp(data.timestamp);
  if (listing && (data.currency !== 'USD' || !sourceTimestamp)) return null;
  const timestamp = sourceTimestamp ?? receivedAt;
  if (timestamp > receivedAt + 60_000) return null;

  const bid = positiveNumber(data.bid);
  const ask = positiveNumber(data.ask);
  let mid: number;
  if (bid !== null && ask !== null) {
    if (bid > ask) return null;
    mid = (bid + ask) / 2;
  } else {
    mid = positiveNumber(data.price ?? data.mid) ?? 0;
  }
  if (!Number.isFinite(mid) || mid <= 0) return null;
  return { symbol, bid: bid ?? mid, ask: ask ?? mid, mid, timestamp,
    ...(typeof data.currency === 'string' ? { currency: data.currency } : {}) };
}

export function parseValoreData(data: unknown, receivedAt = Date.now()): ValoreQuote[] {
  if (!data || typeof data !== 'object') return [];
  const entries: [string, unknown][] = Array.isArray(data)
    ? data.filter(item => item && typeof item.symbol === 'string').map(item => [item.symbol, item])
    : Object.entries(data);
  return entries.flatMap(([symbol, value]) => {
    const quote = parseValoreQuote(symbol, value, receivedAt);
    return quote ? [quote] : [];
  });
}

export function toValoreMarketRow(quote: ValoreQuote) {
  return {
    symbol: quote.symbol, price: quote.mid, bid_price: quote.bid, ask_price: quote.ask,
    timestamp: new Date(quote.timestamp).toISOString(),
    volume_24h: 0, change_24h: 0, high_price_24h: quote.mid, low_price_24h: quote.mid,
    market_cap: 0, funding_rate: 0, open_interest: 0,
  };
}
